const DEBUG_LOGS = process.env.NODE_ENV !== "production";

const STATSIG_CLIENT_KEY = "client-NbcJ21z8SP2jEjdwsLgxqJKceg7a88rKwSya6HL4Q4I";

let statsigClient: any = null;

// Cookie helpers for cross-subdomain Statsig user ID sharing
function getStatsigUserIdFromCookie(): string | null {
  try {
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const [name, cookieValue] = cookie.trim().split("=");
      if (name === "statsig_user_id" && cookieValue) {
        return cookieValue;
      }
    }
  } catch {
    // Ignore cookie access errors
  }
  return null;
}

function setStatsigUserIdCookie(userId: string): void {
  try {
    const isProduction =
      window.location.hostname === "usesparrow.com" ||
      window.location.hostname.endsWith(".usesparrow.com");
    const domain = isProduction ? ".usesparrow.com" : "";

    const cookieString = [
      `statsig_user_id=${userId}`,
      `path=/`,
      `max-age=${(365 * 24 * 60 * 60).toString()}`, // 1 year
      `SameSite=Lax`,
      domain ? `domain=${domain}` : "",
      isProduction ? "Secure" : "",
    ]
      .filter(Boolean)
      .join("; ");

    document.cookie = cookieString;
  } catch {
    // Ignore cookie access errors
  }
}

function getOrCreateStatsigUserId(): string {
  // 1. Check cookie first (cross-subdomain)
  const fromCookie = getStatsigUserIdFromCookie();
  if (fromCookie) return fromCookie;

  // 2. Generate new ID and save to cookie
  const generated =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? (crypto as any).randomUUID()
      : Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
  setStatsigUserIdCookie(generated);
  return generated;
}

// Helper function for conditional logging
const log = (...args: any[]) => {
  if (DEBUG_LOGS) {
    console.log(...args);
  }
};

const warn = (...args: any[]) => {
  if (DEBUG_LOGS) {
    console.warn(...args);
  }
};

const error = (...args: any[]) => {
  // Always log errors
  console.error(...args);
};

class StatsigService {
  private static instance: StatsigService;

  static getInstance(): StatsigService {
    if (!StatsigService.instance) {
      StatsigService.instance = new StatsigService();
    }
    return StatsigService.instance;
  }

  async initialize(userID?: string): Promise<void> {
    // Use provided userID, or get/create from cross-subdomain cookie
    const resolvedUserID = userID || getOrCreateStatsigUserId();
    log("🎬 [Statsig] Starting web initialization...", { userID: resolvedUserID });

    if (statsigClient) {
      log("✅ [Statsig] Already initialized, returning existing client");
      return;
    }

    try {
      log(
        "🔧 [Statsig] Creating StatsigClient with key:",
        STATSIG_CLIENT_KEY.substring(0, 20) + "..."
      );

      // Lazy load web-only modules
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const { StatsigClient } = require("@statsig/js-client");
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const { StatsigSessionReplayPlugin } = require("@statsig/session-replay");
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const { runStatsigAutoCapture } = require("@statsig/web-analytics");

      statsigClient = new StatsigClient(
        STATSIG_CLIENT_KEY,
        { userID: resolvedUserID },
        {
          environment: {
            tier:
              process.env.NODE_ENV !== "production"
                ? "development"
                : "production",
          },
          plugins: [new StatsigSessionReplayPlugin()],
        }
      );

      log("🎥 [Statsig] Initializing Session Replay with config:", {
        sampling: "10% (mousemove: false, scroll: 150ms)",
        privacy: "maskAllInputs: true",
      });

      // Initialize Session Replay with privacy settings
      // runStatsigSessionReplay(statsigClient, {
      //   rrwebConfig: {
      //     sampling: {
      //       mousemove: false,
      //       mouseInteraction: true,
      //       scroll: 150,
      //       media: 800,
      //       input: "last",
      //     },
      //     maskAllInputs: true,
      //     recordCanvas: false,
      //     inlineStylesheet: true,
      //     blockClass: "sr-block",
      //     ignoreClass: "sr-ignore",
      //   },
      // });

      log("📊 [Statsig] Session Replay initialized successfully");

      runStatsigAutoCapture(statsigClient);
      log("🎯 [Statsig] Auto Capture initialized");

      await statsigClient.initializeAsync();
      log("✅ [Statsig] Web client initialization complete!");
    } catch (err) {
      error("❌ [Statsig] Initialization failed:", err);
      throw err;
    }
  }

  updateUser(userID: string): void {
    log("👤 [Statsig] Updating user ID:", userID);
    if (statsigClient) {
      statsigClient.updateUserSync({ userID });
      log("✅ [Statsig] User updated successfully");
    } else {
      warn("⚠️ [Statsig] Client not initialized, cannot update user");
    }
  }

  logEvent(
    eventName: string,
    value?: string | number,
    metadata?: Record<string, any>
  ): void {
    log("📝 [Statsig] Logging event:", eventName, { value, metadata });
    if (statsigClient) {
      statsigClient.logEvent(eventName, value, metadata);
    } else {
      warn("⚠️ [Statsig] Client not initialized, event not logged");
    }
  }

  getClient(): any {
    return statsigClient;
  }
}

export default StatsigService.getInstance();
